import { Typography } from '@mui/material'
import { C1Alert, C1AlertTitle } from 'c1ui'
import { parseEmailString } from 'lib/utils/email-parser'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { StringChipInput, StringChipInputProps } from './string'

type EmailChipInputProps = Omit<StringChipInputProps, 'error' | 'helperText'> & {
    'aria-label'?: string
}

export const EmailChipInput = ({ value, setValue, ...rest }: EmailChipInputProps): React.ReactElement => {
    const [error, setError] = useState<boolean>(false)
    const [helperText, setHelperText] = useState<React.ReactNode>()
    const intl = useIntl()

    return (
        <StringChipInput
            error={error}
            value={value}
            helperText={helperText}
            setValue={(values: string[]): void => {
                const validEmails: string[] = []
                const invalidInputs: string[] = []

                values.forEach((v) => {
                    const parsed = parseEmailString(v)
                    if (parsed.isValid) {
                        // Deduplicate by checking if email already exists
                        if (!validEmails.includes(parsed.email)) {
                            validEmails.push(parsed.email)
                        }
                    } else if (v.trim()) {
                        // Only track non-empty invalid inputs
                        invalidInputs.push(v.trim())
                    }
                })

                // Show errors for invalid inputs
                if (invalidInputs.length > 0) {
                    setError(true)
                    const errorMessage = invalidInputs.length === 1
                        ? intl.formatMessage(
                            {
                                id: 'component_email_chip_input_error_single',
                                defaultMessage: "''{input}'' is not a valid email address",
                            },
                            { input: invalidInputs[0] },
                        )
                        : intl.formatMessage(
                            {
                                id: 'component_email_chip_input_error_multiple',
                                defaultMessage: '{count} invalid email addresses: {emails}',
                            },
                            {
                                count: invalidInputs.length,
                                emails: invalidInputs.slice(0, 3).join(', ') + (invalidInputs.length > 3 ? '...' : ''),
                            },
                        )

                    setHelperText(
                        <C1Alert severity="error">
                            <C1AlertTitle>
                                <FormattedMessage
                                    id="component_email_chip_input_error_title"
                                    defaultMessage="Invalid Email"
                                />
                            </C1AlertTitle>
                            <Typography>{errorMessage}</Typography>
                        </C1Alert>,
                    )
                } else {
                    setError(false)
                    setHelperText(undefined)
                }

                if (setValue) {
                    setValue(validEmails)
                }
            }}
            {...rest}
        />
    )
}
